import { Dimensions, FitType } from 'types';
import { Pixels } from 'utils/measurement';
import {
  fitElement,
  isFill,
  measurementToPx,
  measurementToViewport,
  numberOrPxToPx,
} from 'utils/placement';
import { TemplateAction } from '../../action-types';
import { VideoTemplateStateContent } from '../../types';
import {
  addVideo,
  deleteVideo,
  recropVideoClip,
  replaceVideo,
  scaleVideoClip,
  setInitialVideoClipPlacement,
  updateVideoBlur,
  updateVideoClipPlacement,
  updateVideoFramingMethod,
} from './mutations';

export default function reducer(
  state: VideoTemplateStateContent,
  action: TemplateAction,
): VideoTemplateStateContent {
  switch (action.type) {
    case 'VIDEOCLIP_ADD': {
      const {
        src,
        original,
        fileName,
        integrationData,
        id,
        enableBlur,
        fitType,
        framingMethod,
        trimStartMillis,
        trimEndMillis,
        metadata,
      } = action.payload;

      return addVideo(
        state,
        src,
        {
          original,
          fileName,
          integrationData,
          id,
          enableBlur,
          fitType,
          framingMethod,
          trimStartMillis,
          trimEndMillis,
        },
        metadata,
      );
    }

    case 'VIDEOCLIP_DELETE': {
      const { id } = action.payload;
      return deleteVideo(state, id);
    }

    case 'VIDEOCLIP_LAYER_DOUBLE_CLICK': {
      const { id, fitType } = action.payload;
      const { canvas, videoClips } = state;
      const videoClip = videoClips.data[id];

      const canvasPx = {
        height: new Pixels(canvas.height),
        width: new Pixels(canvas.width),
      };

      const placement: Dimensions<Pixels> = measurementToPx(
        videoClip?.placement,
        canvasPx,
      );

      const newPlacement = fitElement(
        placement,
        canvasPx,
        fitType || (isFill(placement, canvasPx) ? 'fit' : 'fill'),
      );

      const newPlacementVp = measurementToViewport(newPlacement, canvasPx);

      return updateVideoClipPlacement(
        state,
        id,
        newPlacementVp as any,
        fitType || (isFill(placement, canvasPx) ? 'fit' : 'fill'),
      );
    }

    case 'VIDEOCLIP_PLACEMENT_CHANGE': {
      const { id, placement } = action.payload;
      const { canvas, videoClips } = state;
      const slide = videoClips?.data[id];

      const canvasPx = numberOrPxToPx({
        height: canvas.height,
        width: canvas.width,
      });

      if (typeof placement === 'string') {
        return updateVideoClipPlacement(
          state,
          id,
          fitElement(
            measurementToPx(slide.placement, canvasPx),
            canvasPx,
            placement as FitType,
          ),
        );
      }

      return updateVideoClipPlacement(
        state,
        id,
        placement as Dimensions<Pixels>,
      );
    }

    case 'VIDEO_BLUR_CHANGE': {
      const { id, enableBlur } = action.payload;
      return updateVideoBlur(state, id, enableBlur);
    }

    case 'VIDEOCLIP_REPLACE': {
      const {
        src,
        original,
        fileName,
        integrationData,
        id,
        enableBlur,
        framingMethod,
        fitType,
        trimStartMillis,
        trimEndMillis,
        previewType,
      } = action.payload;
      return replaceVideo(state, id, src, previewType, {
        original,
        fileName,
        integrationData,
        enableBlur,
        framingMethod,
        fitType,
        trimStartMillis,
        trimEndMillis,
      });
    }

    case 'VIDEOCLIP_CROP': {
      const { id, metadata } = action.payload;
      return recropVideoClip(state, id, metadata);
    }

    case 'VIDEOCLIP_LOAD': {
      const { id, position, size } = action.payload;
      return setInitialVideoClipPlacement(state, id, position, size);
    }

    case 'VIDEOCLIP_LAYER_WHEEL': {
      const { event, id } = action.payload;
      return scaleVideoClip(state, id, event.deltaY * 0.001);
    }

    case 'VIDEO_FRAMING_METHOD_CHANGE': {
      const { id, framingMethod } = action.payload;

      return updateVideoFramingMethod(state, id, framingMethod);
    }

    default:
      return state;
  }
}
